import { apiGet, apiPut } from "../_apiShim";
import {
  SetupPlan,
  SetupSectionKey,
  SetupSectionUpdate,
} from "@/types/SetupPlan";

const BASE_PATH = "/api/v1/event-setup-plans/";

/**
 * The event's setup plan, or null when the event has none (created before the
 * setup journey existed) — callers redirect instead of erroring on that.
 */
export async function getSetupPlan(eventId: string): Promise<SetupPlan | null> {
  try {
    const response = await apiGet<SetupPlan>(
      `${BASE_PATH}${eventId}`,
      { "Cache-Control": "no-store" },
      true,
    );
    // "No plan" arrives as 204 No Content — axios surfaces that as an empty
    // string / undefined body. Anything that isn't a plan object is "no plan".
    if (!response || typeof response !== "object") return null;
    return response;
  } catch (e) {
    // Older backends still answer 404 for the absent-plan case — tolerate it so
    // the client works either side of the 404→204 rollout.
    if (e instanceof Error && e.message === "notFound") return null;
    throw e;
  }
}

/** Full upsert (create-event flow). Returns the stored plan. */
export async function putSetupPlan(eventId: string, plan: SetupPlan): Promise<SetupPlan> {
  const response = await apiPut<SetupPlan>(
    `${BASE_PATH}${eventId}`,
    {
      sourceConversationId: plan.sourceConversationId,
      directionId: plan.directionId,
      sections: plan.sections,
    },
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response) throw new Error("unknown");

  return response;
}

/** Partial single-section update (status/brief/proposal/resultLinks). */
export async function putSetupPlanSection(
  eventId: string,
  key: SetupSectionKey,
  update: SetupSectionUpdate,
): Promise<SetupPlan> {
  const response = await apiPut<SetupPlan>(
    `${BASE_PATH}${eventId}/sections/${key}`,
    update,
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response) throw new Error("unknown");

  return response;
}
